/**
 * User Utilities - Centralized user document management
 * 
 * This file ensures ALL user documents have a consistent structure
 * regardless of how they sign up (Google OAuth, Email/Password, etc.)
 */

import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Complete user document structure with default values
 * This ensures every user has ALL required fields
 */
export const getUserDocumentStructure = (user, additionalData = {}) => {
  return {
    // Core identity fields
    uid: user.uid,
    username: user.displayName || additionalData.username || '',
    email: user.email || additionalData.email || '',
    photoURL: user.photoURL || additionalData.photoURL || '',
    
    // Contact & location fields
    mobile: additionalData.mobile || '',
    address: additionalData.address || '',
    
    // Academic fields
    class: additionalData.class || '',
    preparingFor: additionalData.preparingFor || '',
    
    // Role & permissions (always default to false for new users)
    isAdmin: additionalData.isAdmin !== undefined ? additionalData.isAdmin : false,
    isSuperAdmin: additionalData.isSuperAdmin !== undefined ? additionalData.isSuperAdmin : false,
    
    // Legacy fields (if needed by existing code)
    testsTaken: additionalData.testsTaken || [],
    
    // Timestamps
    createdAt: additionalData.createdAt || serverTimestamp(),
    
    // Merge any other additional data
    ...additionalData
  };
};

/**
 * Create or update user document in Firestore
 * This function ensures ALL fields exist and are properly initialized
 * 
 * @param {Object} user - Firebase Auth user object
 * @param {Object} additionalData - Additional user data to merge
 * @param {boolean} forceUpdate - If true, overwrites existing fields; if false, only adds missing fields
 */
export const ensureCompleteUserDocument = async (user, additionalData = {}, forceUpdate = false) => {
  try {
    const userRef = doc(db, 'users', user.uid);
    
    // Check if user document already exists
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists() && !forceUpdate) {
      // User exists - only add missing fields (preserve existing data)
      const existingData = userSnap.data();
      const completeStructure = getUserDocumentStructure(user, additionalData);
      
      // Create an object with only the missing fields
      const missingFields = {};
      Object.keys(completeStructure).forEach(key => {
        if (existingData[key] === undefined || existingData[key] === null) {
          missingFields[key] = completeStructure[key];
        }
      });
      
      // If there are missing fields, update the document
      if (Object.keys(missingFields).length > 0) {
        await setDoc(userRef, missingFields, { merge: true });
        console.log('✅ Added missing fields to user document:', Object.keys(missingFields));
      }
    } else {
      // User doesn't exist OR forceUpdate is true - create/update with complete structure
      const completeUserData = getUserDocumentStructure(user, additionalData);
      await setDoc(userRef, completeUserData, { merge: true });
      console.log('✅ Created/Updated complete user document for:', user.email);
    }
    
    return true;
  } catch (error) {
    console.error('❌ Error ensuring user document:', error);
    throw error;
  }
};

/**
 * Update specific fields in user document
 * This is useful when users update their profile
 * 
 * @param {string} uid - User ID
 * @param {Object} updates - Fields to update
 */
export const updateUserDocument = async (uid, updates) => {
  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, updates, { merge: true });
    console.log('✅ Updated user document:', uid);
    return true;
  } catch (error) {
    console.error('❌ Error updating user document:', error);
    throw error;
  }
};
